import { Alert, Button, Card, Col, Descriptions, Modal, Row, Space, Spin, Tag, Typography } from 'antd'
import { useState } from 'react'
import { useQuickBooksInvoicePaymentsQuery, useReapplyQuickBooksPaymentMutation } from './quickBooksResyncQueries'
import {
  PaymentDiagnosticView,
  getInvoicePaymentsErrorMessage,
  getInvoicePaymentsTitle,
  getReapplyConfirmText,
  getReapplyErrorMessage,
  getReapplySuccessMessage,
  isReapplyDisabled,
  noPaymentsMessage,
  toPaymentDiagnosticViews,
} from './quickBooksPaymentsModel'
import RelinkPaymentModal from './RelinkPaymentModal'

const { Paragraph, Text } = Typography

type Props = {
  tenantId: number
  invoiceId: string
  invoiceNumber: string
  onClose: () => void
}

type QboSideProps = {
  view: PaymentDiagnosticView
  invoiceCustomerName: string
}

const QboSide = ({ view, invoiceCustomerName }: QboSideProps) => {
  if (view.qbo === null && view.exportedToDifferentRealm) {
    return (
      <Alert
        type="warning"
        showIcon
        message={`QBO payment ${view.qboPaymentId} is in a different QuickBooks company`}
        description="It was exported to a QuickBooks company other than the one now connected, so it can't be read here."
      />
    )
  }
  if (view.qbo === null) {
    return <Text type="secondary">This payment was never exported to QuickBooks.</Text>
  }
  if (!view.qbo.exists) {
    return (
      <Alert
        type="error"
        showIcon
        message={`QBO payment ${view.qboPaymentId} no longer exists`}
        description="It was deleted in QuickBooks after SynkedUP exported it."
      />
    )
  }
  return (
    <Descriptions column={1} size="small" bordered>
      <Descriptions.Item label="Customer">
        {view.qbo.customerName}
        {view.hasCustomerMismatch && (
          <div>
            <Text type="danger">Invoice is billed to {invoiceCustomerName}</Text>
          </div>
        )}
      </Descriptions.Item>
      <Descriptions.Item label="Total">{view.qbo.total}</Descriptions.Item>
      <Descriptions.Item label="Unapplied">{view.qbo.unappliedAmount}</Descriptions.Item>
      <Descriptions.Item label="Applied to">
        {view.qbo.appliedInvoices.length === 0
          ? <Text type="secondary">No invoices</Text>
          : view.qbo.appliedInvoices.map(applied => (
            <Tag key={applied.key} color={applied.isThisInvoice ? 'green' : 'orange'}>{applied.label}</Tag>
          ))}
      </Descriptions.Item>
      <Descriptions.Item label="SyncToken">{view.qbo.syncToken}</Descriptions.Item>
      <Descriptions.Item label="Created">{view.qbo.created}</Descriptions.Item>
      <Descriptions.Item label="Last updated">{view.qbo.lastUpdated}</Descriptions.Item>
    </Descriptions>
  )
}

const InvoicePaymentsModal = ({ tenantId, invoiceId, invoiceNumber, onClose }: Props) => {
  const [modal, modalContextHolder] = Modal.useModal()
  const [relinkView, setRelinkView] = useState<PaymentDiagnosticView | undefined>(undefined)
  const [successMessage, setSuccessMessage] = useState<string | undefined>(undefined)
  const paymentsQuery = useQuickBooksInvoicePaymentsQuery(tenantId, invoiceId)
  const reapplyMutation = useReapplyQuickBooksPaymentMutation()

  const views = paymentsQuery.isSuccess
    ? toPaymentDiagnosticViews(paymentsQuery.data.payments, paymentsQuery.data.qboInvoiceId)
    : []
  const title = paymentsQuery.isSuccess
    ? getInvoicePaymentsTitle(paymentsQuery.data.invoiceNumber, paymentsQuery.data.qboDocNumber)
    : getInvoicePaymentsTitle(invoiceNumber, null)
  const invoiceCustomerName = paymentsQuery.data?.qboCustomerName ?? ''
  const qboDocNumber = paymentsQuery.data?.qboDocNumber ?? null
  const qboInvoiceId = paymentsQuery.data?.qboInvoiceId ?? ''
  const reapplyingPaymentId = reapplyMutation.isLoading ? reapplyMutation.variables?.paymentId : undefined

  const handleReapply = (view: PaymentDiagnosticView) => {
    setSuccessMessage(undefined)
    reapplyMutation.reset()
    modal.confirm({
      title: 'Re-apply this payment in QuickBooks?',
      okText: 'Re-apply',
      cancelText: 'Cancel',
      width: 540,
      content: (
        <div>
          <Paragraph strong style={{ marginBottom: 8 }}>
            {getReapplyConfirmText(view, qboDocNumber, qboInvoiceId)}
          </Paragraph>
          <Paragraph style={{ marginBottom: 0 }}>
            This writes to the tenant&apos;s QuickBooks books: the payment&apos;s unapplied amount drops by{' '}
            {view.amount} and the QuickBooks invoice balance drops by the same amount.
          </Paragraph>
        </div>
      ),
      onOk: () => {
        reapplyMutation.mutate(
          { tenantId, invoiceId, paymentId: view.paymentId, qboPaymentId: view.qboPaymentId, syncToken: view.syncToken },
          {
            onSuccess: () => {
              setSuccessMessage(getReapplySuccessMessage(view, qboDocNumber, qboInvoiceId))
            },
          }
        )
      },
    })
  }

  const handleClose = () => {
    if (reapplyMutation.isLoading) {
      return
    }
    onClose()
  }

  const handleOpenRelink = (view: PaymentDiagnosticView) => {
    setSuccessMessage(undefined)
    reapplyMutation.reset()
    setRelinkView(view)
  }

  const handleRelinked = (message: string) => {
    setSuccessMessage(message)
    setRelinkView(undefined)
  }

  return (
    <Modal
      open
      title={title}
      width={960}
      onCancel={handleClose}
      closable={!reapplyMutation.isLoading}
      maskClosable={!reapplyMutation.isLoading}
      keyboard={!reapplyMutation.isLoading}
      footer={[<Button key="close" onClick={handleClose} disabled={reapplyMutation.isLoading}>Close</Button>]}
    >
      {modalContextHolder}
      <Paragraph type="secondary">
        Each SynkedUP payment next to the live QuickBooks payment it was exported as. A payment that
        no longer settles this invoice in QuickBooks leaves a balance open there even when SynkedUP
        shows it paid.
      </Paragraph>

      {paymentsQuery.isLoading && (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin tip="Loading payments from QuickBooks..." />
        </div>
      )}

      {paymentsQuery.isError && (
        <Alert
          type="error"
          showIcon
          message="Failed to load payments"
          description={getInvoicePaymentsErrorMessage(paymentsQuery.error)}
        />
      )}

      {paymentsQuery.isSuccess && views.length === 0 && (
        <Alert type="info" showIcon message={noPaymentsMessage} />
      )}

      {successMessage !== undefined && (
        <Alert type="success" showIcon style={{ marginBottom: 16 }} message={successMessage} />
      )}

      {reapplyMutation.isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Re-apply failed"
          description={getReapplyErrorMessage(reapplyMutation.error)}
        />
      )}

      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        {views.map(view => (
          <Card
            key={view.key}
            size="small"
            title={`${view.title} · ${view.amount}`}
            extra={view.chips.map(chip => (
              <Tag key={chip.label} color={chip.color}>{chip.label}</Tag>
            ))}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Paragraph strong style={{ marginBottom: 8 }}>SynkedUP</Paragraph>
                <Descriptions column={1} size="small" bordered>
                  <Descriptions.Item label="Method">{view.method}</Descriptions.Item>
                  <Descriptions.Item label="Check # / last four">{view.reference}</Descriptions.Item>
                  <Descriptions.Item label="Amount">{view.amount}</Descriptions.Item>
                  <Descriptions.Item label="Status">{view.status}</Descriptions.Item>
                  <Descriptions.Item label="Recorded">{view.recorded}</Descriptions.Item>
                  <Descriptions.Item label="QBO payment ID">{view.qboPaymentId}</Descriptions.Item>
                </Descriptions>
              </Col>
              <Col span={12}>
                <Paragraph strong style={{ marginBottom: 8 }}>QuickBooks</Paragraph>
                <QboSide view={view} invoiceCustomerName={invoiceCustomerName} />
              </Col>
            </Row>
            <Space style={{ marginTop: 12 }} wrap>
              <Button
                size="small"
                type="primary"
                onClick={() => handleReapply(view)}
                disabled={isReapplyDisabled(view, reapplyingPaymentId)}
                loading={reapplyingPaymentId === view.paymentId}
              >
                Re-apply to invoice
              </Button>
              <Button size="small" onClick={() => handleOpenRelink(view)} disabled={reapplyMutation.isLoading}>
                Relink payment
              </Button>
              {view.reapplyUnavailableReason !== null && (
                <Text type="secondary">Re-apply unavailable: {view.reapplyUnavailableReason}</Text>
              )}
            </Space>
          </Card>
        ))}
      </Space>

      {relinkView !== undefined && (
        <RelinkPaymentModal
          key={relinkView.paymentId}
          tenantId={tenantId}
          invoiceId={invoiceId}
          paymentId={relinkView.paymentId}
          paymentTitle={relinkView.title}
          paymentAmount={relinkView.amount}
          onClose={() => setRelinkView(undefined)}
          onRelinked={handleRelinked}
        />
      )}
    </Modal>
  )
}

export default InvoicePaymentsModal
